import React, { Suspense, createContext, lazy, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme, ThemeProvider, createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import TimeEventLoading from '../Timevents/TimeEventLoadingSimple';
import { useWebSocket } from '../WebSocketContext';
import {
  VIEWER_TAG_HOVER_EVENT,
  buildTagHoverEventDetail,
} from './TagHighlightUtils';

export const getViewerTypeFromFilename = (filename = '') => {
  const extension = String(filename).toLowerCase().split('?')[0].split('.').pop();
  if (extension === 'pdf') return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg'].includes(extension)) return 'image';
  if (['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac'].includes(extension)) return 'audio';
  if (['xlsx', 'xls', 'xlsm'].includes(extension)) return 'excel';
  if (['html', 'htm'].includes(extension)) return 'html';
  if (['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'm4v', '3gp', 'm3u8'].includes(extension)) return 'video';
  if (['msg', 'eml'].includes(extension)) return 'email';
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension)) return 'zip';
  if (['docx', 'doc'].includes(extension)) return 'word';
  if (['pptx', 'ppt'].includes(extension)) return 'powerpoint';
  if (extension === 'csv') return 'csv';
  if (extension === 'json') return 'json';
  if (['txt', 'ini', 'hl7', 'log', 'md', 'xml', 'js', 'py', 'java', 'cpp', 'cs', 'php', 'rb', 'go', 'rs', 'swift', 'kt', 'ts', 'jsx', 'tsx', 'vue', 'css', 'scss', 'sass', 'less', 'sql', 'sh', 'bat', 'ps1', 'yaml', 'yml', 'toml', 'cfg', 'conf', 'properties'].includes(extension)) return 'text';
  return 'other';
};

const PDFViewer = lazy(() => import('./PDFViewer'));
const ImageViewer = lazy(() => import('./ImageViewer'));
const OtherViewer = lazy(() => import('./OtherViewer'));
const AudioViewer = lazy(() => import('./AudioViewer'));
const ExcelViewer = lazy(() => import('./ExcelViewer'));
const HTMLViewer = lazy(() => import('./HTMLViewer'));
const TextViewer = lazy(() => import('./TextViewer'));
const VideoViewer = lazy(() => import('./VideoViewer'));
const EmailViewer = lazy(() => import('./EmailViewer'));
const ZipViewer = lazy(() => import('./ZipViewer'));
const WordViewer = lazy(() => import('./WordViewer'));
const PowerPointViewer = lazy(() => import('./PowerPointViewer'));
const CSVViewer = lazy(() => import('./CSVViewer'));
const JSONViewer = lazy(() => import('./JSONViewer'));

const VIEWER_COMPONENTS = {
  pdf: PDFViewer,
  image: ImageViewer,
  other: OtherViewer,
  audio: AudioViewer,
  excel: ExcelViewer,
  html: HTMLViewer,
  text: TextViewer,
  video: VideoViewer,
  email: EmailViewer,
  zip: ZipViewer,
  word: WordViewer,
  powerpoint: PowerPointViewer,
  csv: CSVViewer,
  json: JSONViewer,
};

const FileViewerContext = createContext({
  openFileViewer: () => {},
  scheduleFileViewerOpen: () => {},
  cancelScheduledViewerOpen: () => {},
  closeFileViewer: () => {},
  activeViewer: null,
});

export const useFileViewer = () => useContext(FileViewerContext);

const GlobalFileViewer = React.memo(({ viewer, onClose, isDockedLayout, onTagHover, onTagHoverEnd, isSwitchPending }) => {
  const parentTheme = useTheme();
  const { t } = useTranslation();
  
  const viewerTheme = useMemo(() => {
    if (!isDockedLayout) return parentTheme;
    return createTheme(parentTheme, {
      components: {
        MuiDialog: {
          styleOverrides: {
            root: {
              pointerEvents: 'none',
              '& .MuiBackdrop-root': {
                display: 'none !important',
              }
            },
            container: {
              alignItems: 'stretch',
              justifyContent: 'flex-start',
            },
            paper: {
              pointerEvents: 'auto',
              position: 'fixed !important',
              top: '16px !important',
              left: '16px !important',
              bottom: '16px !important',
              margin: '0 !important',
              width: 'min(42vw, 680px) !important',
              maxWidth: '42vw !important',
              maxHeight: 'calc(100vh - 32px) !important',
              height: 'calc(100vh - 32px) !important',
              borderRadius: '16px !important',
              boxShadow: '0 24px 72px rgba(15, 23, 42, 0.28), 0 8px 24px rgba(87, 87, 176, 0.18) !important',
            }
          }
        }
      }
    });
  }, [parentTheme, isDockedLayout]);

  if (!viewer?.type) {
    return null;
  }

  const ViewerComponent = VIEWER_COMPONENTS[viewer.type];
  if (!ViewerComponent) {
    return null;
  }

  return (
    <ThemeProvider theme={viewerTheme}>
      <>
        <Suspense fallback={<TimeEventLoading />}>
          <ViewerComponent
            open
            onClose={onClose}
            filename={viewer.filename || ''}
            eventId={viewer.eventId ?? null}
            filePath={viewer.filePath || ''}
            timeevent={viewer.timeevent || null}
            onTagHover={onTagHover || null}
            onTagHoverEnd={onTagHoverEnd || null}
          />
        </Suspense>
        {isSwitchPending && (
          <Box
            sx={{
              position: 'fixed',
              top: isDockedLayout ? 16 : 0,
              left: isDockedLayout ? 16 : 0,
              right: isDockedLayout ? 'auto' : 0,
              bottom: isDockedLayout ? 16 : 0,
              width: isDockedLayout ? 'min(42vw, 680px)' : 'auto',
              zIndex: 1600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(71, 85, 105, 0.72)',
              backdropFilter: 'grayscale(1) blur(1px)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                color: '#334155',
                boxShadow: '0 4px 16px rgba(15, 23, 42, 0.24)',
              }}
            >
              <CircularProgress size={20} />
              {t('viewer.switching_file', 'Opening selected file...')}
            </Box>
          </Box>
        )}
      </>
    </ThemeProvider>
  );
});

const FileViewerProvider = ({ children }) => {
  const [activeViewer, setActiveViewer] = useState(null);
  const [isSwitchPending, setIsSwitchPending] = useState(false);
  const scheduledViewerRef = useRef(null);
  const theme = useTheme();
  const isDockedLayout = useMediaQuery(theme.breakpoints.up('lg'));
  const { subscribeToMessages } = useWebSocket();

  const closeFileViewer = useCallback(() => {
    if (scheduledViewerRef.current) {
      clearTimeout(scheduledViewerRef.current);
      scheduledViewerRef.current = null;
    }
    setIsSwitchPending(false);
    setActiveViewer(null);
  }, []);

  const openFileViewer = useCallback((viewer) => {
    if (scheduledViewerRef.current) {
      clearTimeout(scheduledViewerRef.current);
      scheduledViewerRef.current = null;
    }
    setIsSwitchPending(false);
    setActiveViewer(viewer);
  }, []);

  const cancelScheduledViewerOpen = useCallback(() => {
    if (scheduledViewerRef.current) {
      clearTimeout(scheduledViewerRef.current);
      scheduledViewerRef.current = null;
    }
    setIsSwitchPending(false);
  }, []);

  const scheduleFileViewerOpen = useCallback((viewer, delay = 500) => {
    cancelScheduledViewerOpen();
    if (!viewer?.filePath || !viewer?.eventId) {
      return;
    }

    const sameEvent = String(activeViewer?.eventId || '') === String(viewer.eventId);
    const sameFile = activeViewer?.filePath === viewer.filePath;
    if (sameEvent && sameFile) {
      return;
    }

    setIsSwitchPending(true);
    scheduledViewerRef.current = setTimeout(() => {
      scheduledViewerRef.current = null;
      setIsSwitchPending(false);
      setActiveViewer(viewer);
    }, delay);
  }, [activeViewer?.eventId, activeViewer?.filePath, cancelScheduledViewerOpen]);
  const dispatchHoveredTag = useCallback((tag) => {
    if (typeof window === 'undefined') {
      return;
    }

    const detail = tag
      ? buildTagHoverEventDetail(tag, activeViewer?.timeevent || { id: activeViewer?.eventId ?? null })
      : null;

    window.dispatchEvent(new CustomEvent(VIEWER_TAG_HOVER_EVENT, { detail }));
  }, [activeViewer?.eventId, activeViewer?.timeevent]);

  React.useEffect(() => {
    if (typeof subscribeToMessages !== 'function') {
      return undefined;
    }

    return subscribeToMessages(['renalyzeTimeevent'], (message = {}) => {
      const payload = message.data || {};
      const updatedTimeevent = payload.data;
      const updatedEventId = updatedTimeevent?.id ?? payload.id ?? message.id;

      if (!updatedTimeevent || updatedEventId == null) {
        return;
      }

      setActiveViewer((previousViewer) => {
        if (!previousViewer || previousViewer.eventId == null) {
          return previousViewer;
        }

        if (String(previousViewer.eventId) !== String(updatedEventId)) {
          return previousViewer;
        }

        return {
          ...previousViewer,
          timeevent: {
            ...(previousViewer.timeevent || {}),
            ...updatedTimeevent,
          },
        };
      });
    });
  }, [subscribeToMessages]);

  useEffect(() => cancelScheduledViewerOpen, [cancelScheduledViewerOpen]);

  const value = useMemo(() => ({
    openFileViewer,
    scheduleFileViewerOpen,
    cancelScheduledViewerOpen,
    closeFileViewer,
    activeViewer,
  }), [openFileViewer, scheduleFileViewerOpen, cancelScheduledViewerOpen, closeFileViewer, activeViewer]);

  return (
    <FileViewerContext.Provider value={value}>
      <Box sx={{ position: 'relative' }}>{children}</Box>
      <GlobalFileViewer 
        viewer={activeViewer} 
        onClose={closeFileViewer} 
        isDockedLayout={isDockedLayout}
        onTagHover={dispatchHoveredTag}
        onTagHoverEnd={() => dispatchHoveredTag(null)}
        isSwitchPending={isSwitchPending}
      />
    </FileViewerContext.Provider>
  );
};

export default FileViewerProvider;
